import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { AuthSplit } from '../../components/auth/AuthSplit';
import { PasswordField } from '../../components/auth/PasswordField';
import { Button } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/Field';
import { Toggle } from '../../components/ui/ChoiceGroup';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { resolveWorkspaces } from '../../utils/authorization';
import { isLocked, onboardingProgress } from '../../utils/auth';
export function SignIn() {
  const {
    login,
    loginThrottle,
    loginLockSeconds,
    returnTo,
    setReturnTo,
    flagEnabled
  } = useAuth();
  const {
    store
  } = useWorkspace();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const locked = isLocked(loginThrottle);
  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    const outcome = await login(identifier, password, remember);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }

    /* Send the person where they were going, then onboarding, then home. */
    const destination = returnTo;
    setReturnTo(null);
    if (destination) {
      navigate(destination, {
        replace: true
      });
      return;
    }
    const progress = onboardingProgress(outcome.identity);
    if (progress.next && !outcome.identity.onboarding.dismissed) {
      navigate('/onboarding', {
        replace: true
      });
      return;
    }

    /*
      Where to next is an authorization question, not a preference. Workspaces
      are resolved from memberships: exactly one opens directly, several offer
      a choice, and none means the account is valid but opens nothing yet.
    */
    const open = resolveWorkspaces(store, outcome.identity);
    if (open.length === 1) navigate(open[0].path, {
      replace: true
    });else if (open.length > 1) navigate('/workspaces', {
      replace: true
    });else navigate('/', {
      replace: true
    });
  }
  return <AuthSplit headline={['Welcome back.', 'Your campus.', 'One account.']} lead="Your journey, your people and your campus — all behind the one Campuzon account you already have." aside={<span className="text-ink-muted" data-id="element-7652" data-mp="3m4608qkwpytu">
          New to Campuzon?{' '}
          <Link to="/signup" className="font-semibold text-brand-700 underline-offset-2 hover:underline" data-id="element-7653" data-mp="3qzlaz9nmbem1">
            Create an account
          </Link>
        </span>} data-id="element-7651" data-mp="ikjnbxvi1mrk">
      <h1 className="text-[clamp(1.6rem,3vw,2.1rem)] font-semibold leading-tight text-ink" data-id="element-7654" data-mp="16anps1wybggz">
        Welcome back.
      </h1>
      <p className="mb-6 mt-3 text-body text-ink-muted" data-id="element-7655" data-mp="2uujue491qt4h">
        Use your username, email address or phone number — whichever you remember.
      </p>
      <form onSubmit={onSubmit} noValidate className="space-y-5" data-id="element-7656" data-mp="2qu9cpubw6e9s">
        <Field label="Username, email or phone" htmlFor="identifier" hint="You do not need to remember which one you signed up with." data-id="element-7657" data-mp="2m1q0b0oxxp6k">
          <Input id="identifier" value={identifier} onChange={event => setIdentifier(event.target.value)} autoComplete="username" inputMode="email" spellCheck={false} aria-invalid={error ? true : undefined} data-id="element-7658" data-mp="2h1c0g5xhtljm" />
        </Field>

        <PasswordField id="password" label="Password" value={password} onChange={setPassword} autoComplete="current-password" data-id="element-7659" data-mp="1y4u41b7p8xh1" />

        <Toggle checked={remember} onChange={setRemember} label="Remember this device" description="Keeps you signed in for 30 days. Leave off on a shared or public device." data-id="element-7660" data-mp="3sibryzc0nwcl" />

        {error && <p role="alert" className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 px-3 py-2.5 text-body-sm text-danger-700" data-id="element-7661" data-mp="2u57pm0yw52kt">
            <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-7662" data-mp="2l4ro2x1pk5il" />
            {error}
          </p>}

        {locked && <p role="status" className="flex items-start gap-2 rounded-md border border-warn-100 bg-warn-50 px-3 py-2.5 text-body-sm text-ink" data-id="element-7663" data-mp="26f3i3iil0ioj">
            <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warn-700" data-id="element-7664" data-mp="1xi30buq0piu2" />
            Too many attempts. Try again in {loginLockSeconds} seconds. This is temporary — your
            account is not locked.
          </p>}

        <Button type="submit" variant="primary" block loading={busy} disabled={locked || !identifier.trim() || !password} data-id="element-7665" data-mp="1shooe1lapoqr">
          Log in
        </Button>

        <p className="text-center text-body-sm" data-id="element-7666" data-mp="13xbxnjsncm6y">
          <Link to="/forgot-password" className="cz-inline-link" data-id="element-7667" data-mp="13ymz3cymdhuw">
            Forgot your password?
          </Link>
        </p>

        {!flagEnabled('oauth_google') && <p className="border-t border-line pt-4 text-caption text-ink-muted" data-id="element-7668" data-mp="1dsvhh7txrufc">
            Google sign-in and passkeys are architected but not configured, so they are not offered
            here rather than shown as buttons that would fail.
          </p>}
      </form>

      <div className="mt-6 rounded-card border border-line bg-canvas p-4" data-id="element-7669" data-mp="21izkpe37ps5t">
        <p className="flex items-start gap-2 text-body-sm font-semibold text-ink" data-id="element-7670" data-mp="1wkei580hmwe7">
          <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" data-id="element-7671" data-mp="2akft5gagvk7e" />
          Development accounts — not real people
        </p>
        <ul className="mt-2 space-y-2 text-body-sm text-ink-muted" data-id="element-7672" data-mp="26kf12sug3ahv">
          {[['amarachi', 'CampusRain2026', 'Active student, also a campus representative'], ['tunde.a', 'OpenGateway77', 'Unverified — signs in with limits'], ['kelechi.n', 'LagoonWalk401', 'Suspended — sign-in is refused']].map(([user, pass, note]) => <li key={user} data-id="element-7673" data-mp="2sbgex5qzy970">
              <button type="button" onClick={() => {
            setIdentifier(user);
            setPassword(pass);
          }} className="cz-num min-h-touch w-full rounded-md border border-line bg-surface px-3 py-2 text-left text-body-sm text-ink transition-colors duration-150 ease-out hover:border-line-strong" data-id="element-7674" data-mp="2scrjv8w6hmzp">
                @{user} · {pass}
                <span className="mt-0.5 block text-caption text-ink-muted" data-id="element-7675" data-mp="3b7q3imziyi59">{note}</span>
              </button>
            </li>)}
        </ul>
      </div>
    </AuthSplit>;
}